/**
 * レーン列 — 縦のカラムが横に並ぶ story map の形。
 *
 * **列は階層の段に固定する**（業務マップはステージ → 業務フロー → 業務 → 業務ルール）。左ほど抽象度が高く、
 * 左のカードを押すと右が絞られる。列が段に固定されているので、カードが増えても列は増えず、
 * 縦に伸びるだけである（ラフ `overview.html` / `lanes.js`）。
 *
 * **画面の幅が足りないときは、レーンを潰さず板ごと横へ流す。** 列の本数は段の数で決まる
 * （業務マップは 4 本、案件の変更一覧は 6 本）。狭い画面では 1 本あたりが 100px を切り、
 * 均等に潰すと見出しも札もカードの文も数文字ずつで折り返して読めなくなる。レーンに最小幅
 * （`LANE_MIN_WIDTH`）を与えて潰れなくし、収まらないぶんは `overflow-x-auto` の器で横へ
 * 流す。流すのは板の中だけで、本文ごと横に伸びて画面全体が横スクロールになる形にはしない
 * （`frontend.md` のレイアウト規約）。幅が足りるときは今までどおり `flex-1` で均等に広がる。
 *
 * 名前と件数しか載らない細い段は、レーンに幅（`Lane.width`）を渡して固定する。均等に広げると、
 * 短い名前の列が他の列と同じ幅を取り、読むべき右の列が狭くなる。
 *
 * **カードは看板形式の型で描く**（引継書 §2「看板形式のカード」）。カード全体を押す ＝ 右のレーンを
 * 絞る（主の操作は 1 つ）。別の画面へ移る口はカード右上の「詳細」ボタン 1 つだけで、見える文字は
 * どのカードも「詳細」、行き先の名前は title と aria-label に載せる。カードの中に文字リンクを置かない
 * — 押す場所によって「絞る」と「移る」が入れ替わると、押す前に何が起きるかが読めない。画面の中で
 * 開く補足（要求の「背景」）は右下の副のボタンに置く。どちらのボタンも押せる本体の外に並べるので、
 * 押しても絞り込みは動かない。
 *
 * **絞る先の無いレーンのカードは押せない**（業務ルール・画面）。カード全体を押して別の画面へ移る口は
 * 置かず、移るのは「詳細」ボタンだけにする（Dhik 2026-09-30「カードを押したときには何も起きないように
 * して。詳細を見るときには詳細ボタンをクリックに統一して」）。以前は業務ルールと画面のカードだけが
 * 押すと移る例外で、同じ板の上で「押すと絞る」「押すと移る」が混ざり、押す前に何が起きるかが読めなかった。
 *
 * レーンの中には、カードのほかに**区切りの見出し**（`LaneHeading`）を置ける。押せない 1 行で、
 * 業務マップのステージレーンが鎖の切れ目に鎖の名前を出すのに使う。名前の要らない切れ目には
 * **文字の無い区切り**（`LaneSeparator`、破線 1 本）を置く。画面一覧のステージレーンが「ステージ未定」を
 * ステージのカードから分けるのに使う。
 *
 * 業務マップと案件の変更一覧が**同じ部品を使う**。違いは「どの要素を出すか」の絞り込み
 * だけで、描き方は同じである。ここが持つのは列と札の描き方だけで、何を出すかは呼び出し側
 * （`overview-selection.ts` / `pj-lanes-build.ts`）が決める。
 *
 * 差分の印は `SpecDiffState.tsx` の語彙と色を使う。ラフが独自に決めた色分けは使わない —
 * 同じ「追加」が画面によって違う色で出ると、画面をまたいだ瞬間に色の意味を学び直すことに
 * なる。印は差分の 4 状態だけで、「配下に変更」は載せない（Dhik 2026-09-29「PJモードの業務マップでも
 * 配下に変更を出さず、色もつけないでほしい」。読取の `descendant` は呼び出し側が変更なしに畳んで渡す）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 階層の段に固定したレーンのカード列を看板形式（押すと右を絞る・右上の詳細ボタンで別の画面へ・右下の副のボタン）で描き、案件の差分の印（追加・変更・削除）を載せ、幅が足りなければ横へ流す。
 * @graph-connects SpecDiffBadge [calls] カードに載せる差分の印
 * @graph-connects specDiffRowClass [calls] 追加・削除されたカードの地色
 */
import { useTranslation } from 'react-i18next';

import { SpecDiffBadge, specDiffRowClass, type SpecDiffState } from './SpecDiffState.js';

/**
 * 「詳細」ボタンを描くときに、リンクの部品へ渡す属性。
 *
 * 見た目・見える文字・title・aria-label は板が決める。リンクの部品に書かせると、行き先ごとに
 * 見える文字や名前の付け方が別々に育ち、「どのカードも『詳細』、名前は title と aria-label」が
 * 崩れる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 「詳細」ボタンを描くときに、板が決めた見た目・行き先の名前・要素の id・中身をリンクへ渡す。
 * @graph-connects none
 */
export interface LaneCardDetailAnchorProps {
  /** ボタンの見た目 */
  className: string;
  /** 行き先の名前（指を載せると出る） */
  title: string;
  /** 読み上げる名前（「詳細: 行き先の名前」） */
  'aria-label': string;
  /** カードが指す要素の id */
  'data-lane-card-detail': number;
  /** 見える文字（「詳細」と矢印） */
  children: React.ReactNode;
}

/**
 * カード右上の「詳細」ボタンの中身（行き先の名前と、行き先へのリンクの描き方）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business カード右上の「詳細」ボタンの行き先の名前と、行き先へのリンクの描き方
 * @graph-connects none
 */
export interface LaneCardDetail {
  /** 行き先の名前（「ステージの図を開く」など）。title と aria-label に載る */
  destination: string;
  /** 行き先へのリンクを描く。見た目・名前・中身は板が渡す */
  link: (anchor: LaneCardDetailAnchorProps) => React.ReactNode;
}

/**
 * 1 枚のカード。押せる（押すと右を絞る）かどうかは、レーンが `onSelect` を持つかで決まる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーンに並ぶカード 1 枚の中身（要素の id・見出し・補足・札・詳細ボタン・副のボタン・案件の差分の状態）
 * @graph-connects none
 */
export interface LaneCard {
  /** カードが指す要素の id */
  id: number;
  /** 見出し */
  title: string;
  /** 見出しの下の 1 行。無ければ描かない */
  subtitle?: string;
  /** 見出しのすぐ下に等幅で出す 1 行（画面の URL パターン）。無ければ描かない */
  codeLine?: string;
  /** 見出しの下に並べる札（型・役割・形・未翻訳等） */
  badges?: React.ReactNode;
  /** 下段に置く補足。無ければ描かない */
  note?: React.ReactNode;
  /**
   * 指すと出す文字（カードの本体の title）。入った直後には見せない補足（件数・URL パターン・
   * 種類など）をここへ回す。無ければ title を付けない。
   */
  hint?: string;
  /**
   * 右上の「詳細」ボタン。**別の画面へ移る口はこれだけにする。**
   *
   * 押せる本体の外に置く。押せるカードは `button` なので、その中にリンクを入れると入れ子が不正に
   * なるうえ、押したときにどちらが起きるのかが利用者にも実装にも決まらない。
   */
  detail?: LaneCardDetail;
  /**
   * 右下の副のボタン（画面の中で開く補足。要求の「背景」）。押しても絞り込みは動かない。
   * 「詳細」と同じく押せる本体の外に置く。
   */
  action?: React.ReactNode;
  /** 案件の差分の状態 */
  diff: SpecDiffState;
  /**
   * 見出しを薄い字で出す（画面一覧のコールバック — 人が見ない中継画面）。省くと通常の字。
   */
  faint?: boolean;
  /**
   * 枠を点線で出す（画面一覧の「ステージ未定」— まとまりではない寄せ場）。省くと実線。
   */
  dashed?: boolean;
}

/**
 * レーンの中の区切りの見出し。押せず、続くカードのまとまりの名前を出すだけである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーンの中に置く区切りの見出し（鍵と文言）
 * @graph-connects none
 */
export interface LaneHeading {
  /** 区切りの見出しであることの印。カードと見分ける */
  kind: 'heading';
  /** 見出しの鍵（レーンの中で一意） */
  headingKey: string;
  /** 見出しの文言 */
  label: string;
}

/**
 * レーンの中の文字の無い区切り（破線 1 本）。押せず、前後のまとまりを分けるだけである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーンの中に置く文字の無い区切り（鍵だけを持つ）
 * @graph-connects none
 */
export interface LaneSeparator {
  /** 文字の無い区切りであることの印。カードと区切りの見出しと見分ける */
  kind: 'separator';
  /** 区切りの鍵（レーンの中で一意） */
  separatorKey: string;
}

/**
 * レーンに並べるもの（カード・区切りの見出し・文字の無い区切り）。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーンに並べるもの（カード・区切りの見出し・文字の無い区切り）
 */
export type LaneItem = LaneCard | LaneHeading | LaneSeparator;

/**
 * 並べるものが区切りの見出しかどうか。
 *
 * @param item レーンに並べるもの
 * @returns 区切りの見出しなら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーンに並べるものが区切りの見出しかを見分ける。
 * @graph-connects none
 */
export function isLaneHeading(item: LaneItem): item is LaneHeading {
  return 'kind' in item && item.kind === 'heading';
}

/**
 * 並べるものが文字の無い区切りかどうか。
 *
 * @param item レーンに並べるもの
 * @returns 文字の無い区切りなら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーンに並べるものが文字の無い区切りかを見分ける。
 * @graph-connects none
 */
export function isLaneSeparator(item: LaneItem): item is LaneSeparator {
  return 'kind' in item && item.kind === 'separator';
}

/** 1 レーン。 @graph-connects none */
export interface Lane {
  /** レーンの鍵（レーンの種別） */
  key: string;
  /** レーンの見出し */
  title: string;
  /**
   * 見出しの右に出す件数。**数えられないレーンは `null`。**
   *
   * 0 で表さない。「業務を選んでいないので数えようがない」と「その業務の処理が 0 件」は
   * 別の状態で、前者を 0 と書くと処理が 1 件も無いように読める。
   */
  count: number | null;
  /**
   * 見出しと件数の行を描くか。省くと描く。`false` のときは見出しを描かず、レーンの名前を
   * 読み上げ用の名前（`aria-label`）にだけ載せる（カードの中身でレーンの種類が分かる画面一覧）。
   */
  header?: boolean;
  /** レーンに並べるカード（区切りの見出しを含められる） */
  cards: readonly LaneItem[];
  /** いま選ばれているカードの id。選んでいなければ `undefined` */
  selectedId?: number;
  /** カードを押したときに呼ぶ（押すと右のレーンを絞る。選択の状態を持つ）。押せないレーンは省く */
  onSelect?: (id: number) => void;
  /** カードが 0 枚のときに出す文言。省くと何も出さない（隣のレーンの空の文が同じことを言うとき） */
  emptyMessage?: string;
  /**
   * レーンの幅（CSS の長さ）。渡すとその幅に固定し、均等には広げない。省くと `flex-1` で
   * 広がり、`LANE_MIN_WIDTH` より狭くはならない。
   */
  width?: string;
  /**
   * カードを格子に並べるときの 1 列の最小幅（CSS の長さ）。渡すとカードを左から右、上から下の
   * 順に格子へ詰め、入るだけ列を増やす。省くと 1 列に縦へ積む。広いレーン（画面一覧の画面）で
   * 縦の長さを抑えるためのもの。
   */
  gridMinColumn?: string;
}

/** `LaneBoard` の props。 */
export interface LaneBoardProps {
  /** 左から順に並べるレーン */
  lanes: readonly Lane[];
  /**
   * レーンの件数を見出しに並べず、見出しの `title` に載せる。省くと見出しの右に件数を出す。
   * 数を判断に使わない画面（業務マップ・案件の変更一覧）が渡す。
   */
  countInTitle?: boolean;
  /**
   * カードの「詳細」と副のボタンを、カードに指を載せたとき（とキーボードで移ったとき）だけ見せる。
   * 省くと常に見せる。全カードに同じ語が並ぶ画面（業務マップ・案件の変更一覧）が渡す。
   */
  controlsOnHover?: boolean;
}

/** レーンの高さ。ラフの 620px を現物の余白に合わせて据え置く。 @graph-connects none */
const LANE_BOARD_HEIGHT = '620px';

/**
 * レーン 1 本の最小幅。設計書の主領域 960px を業務全体像の 4 レーンで割った値
 * （`docs/spec-console/redesign.md` §6 の寸法）。これより狭いとカードの文が数文字で
 * 折り返す。 @graph-connects none
 */
const LANE_MIN_WIDTH = '240px';

/** レーンの見出しの見た目。 @graph-connects none */
const LANE_HEADER_CLASS =
  'flex h-9 flex-none items-center justify-between gap-2 border-b-2 border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 text-xs font-bold tracking-wider text-[var(--color-text-secondary)] uppercase';

/** カード 1 枚の見た目（選択中でないとき）。 @graph-connects none */
const LANE_CARD_CLASS =
  'block w-full border border-[var(--color-border)] border-l-[3px] px-2.5 py-2 text-left text-xs leading-snug';

/**
 * 押せるカードの見た目。ホバーで枠の色を変え、キーボードで移ったときはデザインシステムと
 * 同じフォーカスの輪を出す。押せないカードと見分けが付かないと、押せることに気付けない。
 * @graph-connects none
 */
const LANE_CARD_PRESSABLE_CLASS =
  'cursor-pointer hover:border-[var(--color-border-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-base)]';

/**
 * 「詳細」ボタンの見た目（副のボタン。枠だけで地色を敷かない）。主のボタン（画面に 1 つ）と
 * 見分けが付くよう、地色はホバーでだけ変える。
 * @graph-connects none
 */
const LANE_CARD_DETAIL_CLASS =
  'inline-flex h-[22px] items-center gap-1 rounded-sm border border-[var(--color-border)] bg-[var(--color-bg-base)] px-1.5 text-[11px] font-bold leading-none text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]';

/**
 * レーン列を描く。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 階層の段に固定したレーン列を描き、各レーンの件数とカードを出す。
 * @graph-connects LaneColumn [calls] レーン 1 本の描画
 */
export function LaneBoard({
  lanes,
  countInTitle = false,
  controlsOnHover = false,
}: LaneBoardProps) {
  return (
    <div
      data-lane-board
      className="flex overflow-x-auto border border-[var(--color-border)]"
      style={{ height: LANE_BOARD_HEIGHT }}
    >
      {lanes.map((lane) => (
        <LaneColumn
          key={lane.key}
          lane={lane}
          countInTitle={countInTitle}
          controlsOnHover={controlsOnHover}
        />
      ))}
    </div>
  );
}

/** `LaneColumn` の props。 @graph-connects none */
interface LaneColumnProps {
  lane: Lane;
  /** 件数を見出しの `title` に載せる（`LaneBoardProps.countInTitle`） */
  countInTitle: boolean;
  /** 「詳細」と副のボタンを指を載せたときだけ見せる（`LaneBoardProps.controlsOnHover`） */
  controlsOnHover: boolean;
}

/**
 * 指を載せたときだけ見せるボタンの見た目。見えないあいだも押せる場所とキーボードの移動先には残し、
 * カードに指を載せるか、中のボタンへキーボードで移ったときに見せる。
 * @graph-connects none
 */
const HOVER_ONLY_CLASS = 'opacity-0 group-hover:opacity-100 focus-within:opacity-100';

/**
 * レーン 1 本（見出し・件数・カード）。
 *
 * 件数は見出しの右に出す（`countInTitle` なら見出しの `title` に載せる）。**数えられないレーンは「—」**で、0 とは書き分ける。見出しを描かない
 * レーン（`header: false`）は、レーンの名前を読み上げ用の名前にだけ載せる。
 *
 * 幅は `flex-1` で均等に広がるが、`LANE_MIN_WIDTH` より狭くはならない。`min-w-0` は置かない
 * — 置くと最小幅が 0 になり、板が横へ流れる前にレーンのほうが潰れる。幅を渡されたレーンは
 * その幅に固定する（`flex-none`）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーン 1 本の見出し・件数・カード列を描き、空のときは案内を出す。
 * @graph-connects LaneCardView [calls] カード 1 枚（押せる本体と、外に並べるボタン）
 * @graph-connects isLaneHeading [calls] 並べるものが区切りの見出しかどうか
 * @graph-connects isLaneSeparator [calls] 並べるものが文字の無い区切りかどうか
 * @graph-connects LaneEmptyMessage [calls] カードが 0 枚のときの案内
 */
function LaneColumn({ lane, countInTitle, controlsOnHover }: LaneColumnProps) {
  const fixed = lane.width !== undefined;
  const header = lane.header !== false;
  const count = lane.count ?? '—';

  return (
    <div
      data-lane={lane.key}
      data-lane-count={lane.count ?? 'none'}
      role={header ? undefined : 'group'}
      aria-label={header ? undefined : lane.title}
      className={`flex ${fixed ? 'flex-none' : 'flex-1'} flex-col border-r border-[var(--color-border)] last:border-r-0`}
      style={fixed ? { width: lane.width, minWidth: lane.width } : { minWidth: LANE_MIN_WIDTH }}
    >
      {header && (
        <div className={LANE_HEADER_CLASS} title={countInTitle ? String(count) : undefined}>
          <span>{lane.title}</span>
          {!countInTitle && (
            <span className="text-xs font-normal tabular-nums text-[var(--color-text-muted)]">
              {count}
            </span>
          )}
        </div>
      )}
      <div
        data-lane-grid={lane.gridMinColumn === undefined ? undefined : true}
        className={`flex-1 overflow-auto p-2 ${lane.gridMinColumn === undefined ? '' : 'grid content-start gap-x-1.5'}`}
        style={
          lane.gridMinColumn === undefined || lane.cards.length === 0
            ? undefined
            : { gridTemplateColumns: `repeat(auto-fill, minmax(${lane.gridMinColumn}, 1fr))` }
        }
      >
        {lane.cards.length === 0 ? (
          <LaneEmptyMessage message={lane.emptyMessage} />
        ) : (
          lane.cards.map((item) =>
            isLaneSeparator(item) ? (
              <hr
                key={`separator-${item.separatorKey}`}
                data-lane-separator={item.separatorKey}
                className={LANE_SEPARATOR_CLASS}
              />
            ) : isLaneHeading(item) ? (
              <p
                key={`heading-${item.headingKey}`}
                data-lane-heading={item.headingKey}
                className={LANE_HEADING_CLASS}
              >
                {item.label}
              </p>
            ) : (
              <LaneCardView
                key={item.id}
                card={item}
                selected={lane.selectedId === item.id}
                onSelect={lane.onSelect}
                controlsOnHover={controlsOnHover}
              />
            ),
          )
        )}
      </div>
    </div>
  );
}

/**
 * カードが 0 枚のレーンの案内。文言を省いたレーンでは何も描かない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business カードが 0 枚のレーンに案内の文言を描き、文言を省いたレーンでは何も描かない。
 * @graph-connects none
 */
function LaneEmptyMessage({ message }: { message: string | undefined }) {
  if (message === undefined) return null;
  return <p className="p-3 text-xs leading-relaxed text-[var(--color-text-muted)]">{message}</p>;
}

/**
 * 区切りの見出しの見た目。カードより小さく、上に余白を取ってまとまりの切れ目を示す。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 区切りの見出しの見た目
 * @graph-connects none
 */
const LANE_HEADING_CLASS =
  'col-span-full mt-2 mb-1 px-0.5 text-xs font-bold text-[var(--color-text-secondary)] first:mt-0';

/**
 * 文字の無い区切りの見た目（破線 1 本）。上下に余白を取ってまとまりの切れ目を示す。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 文字の無い区切りの見た目
 * @graph-connects none
 */
const LANE_SEPARATOR_CLASS =
  'col-span-full mt-2 mb-2 border-0 border-t border-dashed border-[var(--color-border)]';

/** `LaneCardView` の props。 @graph-connects none */
interface LaneCardViewProps {
  card: LaneCard;
  selected: boolean;
  onSelect?: (id: number) => void;
  /** 「詳細」と副のボタンを指を載せたときだけ見せる */
  controlsOnHover: boolean;
}

/**
 * カード 1 枚。押すと絞るレーンでは `button`、押せないレーンでは `div` で本体を描き、
 * 「詳細」ボタンと副のボタンを本体の**外**に重ねる。
 *
 * **押せないカードを `button` にしない。** 押しても何も起きない `button` は、キーボードの
 * 移動先にも読み上げの操作対象にもなり、押せるカードと見分けが付かない。業務ルールと画面のカードは
 * 右に絞る先が無いので押せず、別の画面へは「詳細」ボタンでだけ行く。
 *
 * 選択中は枠を太くして地色を変える。地色だけで示すと、差分の印の地色（追加・削除）と
 * 重なって「選んでいる」のか「案件が触った」のかが読めない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business カード 1 枚を、押して絞れるかどうか・選択中か・案件の差分の状態に応じて描き、右上に詳細ボタン・右下に副のボタンを重ねる。
 * @graph-connects LaneCardBody [calls] カード 1 枚の中身
 * @graph-connects laneCardMain [calls] カードの本体（絞る・押せない）の出し分け
 * @graph-connects LaneCardDetailButton [calls] 右上の「詳細」ボタン
 * @graph-connects specDiffRowClass [calls] 追加・削除されたカードの地色
 */
function LaneCardView({ card, selected, onSelect, controlsOnHover }: LaneCardViewProps) {
  const className = [
    LANE_CARD_CLASS,
    selected
      ? 'border-2 border-[var(--color-accent-primary)] bg-[var(--color-bg-active)]'
      : 'bg-[var(--color-bg-base)]',
    specDiffRowClass(card.diff).trim(),
    card.dashed === true ? 'border-dashed' : '',
  ]
    .filter((part) => part !== '')
    .join(' ');

  const main = laneCardMain({
    card,
    selected,
    onSelect,
    className,
    body: <LaneCardBody card={card} />,
  });

  /* 組み立てる文字列の中でクラス名に `${}` を続けない。Tailwind の走査がクラス名を拾えなくなる。 */
  const hoverOnly = controlsOnHover ? HOVER_ONLY_CLASS : '';

  return (
    <div
      data-lane-card-frame={card.id}
      className={`relative mb-1.5 ${controlsOnHover ? 'group' : ''}`}
    >
      {main}
      {card.detail !== undefined && (
        <span className={`absolute top-1.5 right-1.5 ${hoverOnly}`.trim()}>
          <LaneCardDetailButton id={card.id} detail={card.detail} />
        </span>
      )}
      {card.action !== undefined && (
        <span
          data-lane-card-action={card.id}
          className={`absolute right-1.5 bottom-1.5 ${hoverOnly}`.trim()}
        >
          {card.action}
        </span>
      )}
    </div>
  );
}

/** `LaneCardDetailButton` の props。 @graph-connects none */
export interface LaneCardDetailButtonProps {
  /** カードが指す要素の id */
  id: number;
  /** 行き先の名前と描き方 */
  detail: LaneCardDetail;
}

/**
 * カード右上の「詳細」ボタン。見える文字はどのカードも「詳細」、行き先の名前は title と
 * aria-label（「詳細: 行き先の名前」）に載せる。
 *
 * 板の外のカードに「詳細」を置くときも、このボタンをそのまま使う。見た目と名前の付け方を 2 か所で
 * 書くと、「どのカードも『詳細』、名前は title と aria-label」が崩れる。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business カード右上に、見える文字「詳細」と行き先の名前（title・aria-label）を持つリンクのボタンを描く。
 * @graph-connects none
 */
export function LaneCardDetailButton({ id, detail }: LaneCardDetailButtonProps) {
  const { t } = useTranslation();

  return detail.link({
    className: LANE_CARD_DETAIL_CLASS,
    title: detail.destination,
    'aria-label': t('laneBoard.detailLabel', { destination: detail.destination }),
    'data-lane-card-detail': id,
    children: (
      <>
        <span>{t('laneBoard.detail')}</span>
        <svg
          viewBox="0 0 16 16"
          aria-hidden="true"
          className="h-3 w-3 fill-none stroke-current"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9.5 2.5h4v4M13.5 2.5 8 8M12 9.5v4H2.5V4h4" />
        </svg>
      </>
    ),
  });
}

/** `laneCardMain` の入力。 @graph-connects none */
interface LaneCardMainInput {
  card: LaneCard;
  selected: boolean;
  onSelect?: (id: number) => void;
  /** 枠・地色・差分の地色まで組み終えたカードの見た目 */
  className: string;
  /** カードの中身 */
  body: React.ReactNode;
}

/**
 * カードの本体を、絞る・押せないの 2 通りで出し分ける。
 *
 * 絞るカード（`onSelect`）は選択の状態を持つトグルなので `aria-pressed` を付ける。
 *
 * @param input カード・選択中か・押下の受け手（絞る）・見た目・中身
 * @returns カードの本体
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business カードの本体を、絞るトグルのボタン・押せない枠の 2 通りで出し分ける。
 * @graph-connects none
 */
function laneCardMain({
  card,
  selected,
  onSelect,
  className,
  body,
}: LaneCardMainInput): React.ReactNode {
  if (onSelect !== undefined) {
    return (
      <button
        type="button"
        data-lane-card={card.id}
        data-diff-state={card.diff}
        title={card.hint}
        aria-pressed={selected}
        onClick={() => {
          onSelect(card.id);
        }}
        className={`${className} ${LANE_CARD_PRESSABLE_CLASS}`}
      >
        {body}
      </button>
    );
  }

  return (
    <div
      data-lane-card={card.id}
      data-diff-state={card.diff}
      title={card.hint}
      className={className}
    >
      {body}
    </div>
  );
}

/** `LaneCardBody` の props。 @graph-connects none */
interface LaneCardBodyProps {
  card: LaneCard;
}

/**
 * カードの中身（見出し・札・1 行・補足）。
 *
 * 見出しは呼び出し側が決めた文字列をそのまま出す。**未設定と未翻訳の描き分けはここでは
 * しない** — 判定の所有者は `UntranslatedBadge.tsx`（`displayLabel` / `SpecLabelBadges`）で、
 * 部品ごとに書き写すと片方が抜けた行が出る。
 *
 * 右上の「詳細」と右下の副のボタンは本体の外に重なるので、重なる行には右の余白を取って、
 * 見出しや件数がボタンの下に潜らないようにする。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business カードの見出し・札・補足を、外に重なるボタンの下に潜らない余白つきで描く。
 * @graph-connects none
 */
function LaneCardBody({ card }: LaneCardBodyProps) {
  const titlePad = card.detail === undefined ? '' : 'pr-14';
  // 副のボタン（高さ 22px）と同じ高さの行にして、件数の行の横にだけ並ぶようにする
  const actionPad = card.action === undefined ? '' : 'mt-1.5 flex min-h-[22px] items-center pr-16';

  return (
    <>
      <span className={`flex flex-wrap items-center gap-1.5 ${titlePad}`}>
        <span
          className={`font-bold break-words ${card.faint === true ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-text-primary)]'}`}
        >
          {card.title}
        </span>
        <SpecDiffBadge state={card.diff} />
      </span>
      {card.codeLine !== undefined && (
        <span className="mt-0.5 block font-mono break-all text-[var(--color-text-muted)]">
          {card.codeLine}
        </span>
      )}
      {card.badges !== undefined && (
        <span className="mt-1 flex flex-wrap items-center gap-1">{card.badges}</span>
      )}
      {card.subtitle !== undefined && (
        <span className={`mt-0.5 block tabular-nums text-[var(--color-text-muted)] ${actionPad}`}>
          {card.subtitle}
        </span>
      )}
      {card.note !== undefined && (
        <span className="mt-1.5 block border-t border-[var(--color-border)] pt-1.5 text-[var(--color-text-muted)]">
          {card.note}
        </span>
      )}
    </>
  );
}
